import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  RutterExportState,
  SettlementCommissionPresentation,
  SettlementLineItemType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { AddOn } from '~hooks/useAddOns/models';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { useOrders } from '~hooks/useOrders/useOrders';
import useSettlementQuickBooksItems from '~hooks/useRutter/useSettlementQuickBooksItems';
import { SettlementLineItem } from '~hooks/useSettlementLineItems/models';
import useSettlementLineItems from '~hooks/useSettlementLineItems/useSettlementLineItems';
import { Settlement } from '~hooks/useSettlements/models';
import useSettlements from '~hooks/useSettlements/useSettlements';
import { ExpandedBlockTabs } from '~pages/Approvals/Files/components/styled';
import getMappingSelectWidthPx from '~pages/Settings/Integrations/QuickBooksMapping/getMappingSelectWidthPx';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { isSettlementAlreadyExported } from '~utils/settlements/settlement-export-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { AddOnChargesDTO, AddOnChargesFormHandler } from '../../AddOnChargesTableRows';
import { schemaHelpers } from '../../helpers';
import { canSendEmail } from '../helper';
import {
  enrichOrderGroupsWithDisplayData,
  extractUniqueOrderIds,
  fetchOrderDisplayData,
} from '../utils/fetchOrderDetailsForGroups';
import { groupLineItemsByOrderJob, OrderGroup } from '../utils/groupLineItemsByOrderJob';
import {
  ACTION_TRACK_WIDTH_PX,
  COMMON_GRID_CONFIG,
  QUICK_BOOKS_ICON_SLOT_PX,
} from './constants';
import { GroupedSettlementLineItems } from './GroupedSettlementLineItems';
import { QuickBooksItemColumn } from './QuickBooksItemCell';
import SettlementAddOnCharges from './SettlementAddOnCharges';
import SettlementDueDate from './SettlementDueDate';
import SettlementLinesTab from './SettlementLines/SettlementLinesTab';
import SettlementLoadsTab from './SettlementLoadsTab/SettlementLoadsTab';
import SettlementNotes from './SettlementNotes';
import SettlementOutboundEmails from './SettlementOutboundEmails';
import { SettlementPricing } from './SettlementPricing';
import SettlementVoidReason from './SettlementVoidReason';

enum SettlementDetailsTab {
  LINE_ITEMS = 'line_items',
  LOADS = 'loads',
  LINES = 'lines',
}

const tabs = [
  {
    key: SettlementDetailsTab.LINE_ITEMS,
    label: t('approvals.driver_pay.settlements.line_items'),
  },
  { key: SettlementDetailsTab.LOADS, label: t('common.loads') },
];

const linesTab = {
  key: SettlementDetailsTab.LINES,
  label: t('approvals.driver_pay.settlements.lines.tab'),
};

function LoadingPlaceholder() {
  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
        display: 'grid',
        justifyContent: 'center',
        alignContent: 'center',
        height: '100px',
        bgcolor: 'background.paper',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

interface SettlementDetailsProps {
  onSettlementChange: (settlement: Settlement) => void;
  settlement: Settlement;
}

export const SettlementDetails = observer(
  ({ onSettlementChange, settlement }: SettlementDetailsProps) => {
    const settlementId = String(settlement.id);
    const theme = useTheme();
    const [selectedTab, setSelectedTab] = useState(SettlementDetailsTab.LINE_ITEMS);
    const [lineItems, setLineItems] = useState<SettlementLineItem[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const chargeBasedSettlements = useFeatureFlag({
      featureFlagKey: FeatureFlags.chargeBasedSettlements,
    });
    const visibleTabs = chargeBasedSettlements ? [...tabs, linesTab] : tabs;

    const handleSelectedTabChange = useCallback(
      (__: React.SyntheticEvent, value: SettlementDetailsTab) => {
        setSelectedTab(value);
      },
      [],
    );
    const {
      addOnsStore,
      settlementsStore,
      settlementLineItemsStore,
      userStore,
      toasterStore,
    } = useStores();
    const addOnChargesRef = useRef<AddOnChargesFormHandler>({});
    const companyId = getEffectiveUserCompanyId(userStore);
    const isRebill = !!settlement.originalSettlement;
    const presentation =
      settlementLineItemsStore.presentationBySettlementId[settlementId];
    const showNetColumns =
      presentation?.commissionPresentation ===
        SettlementCommissionPresentation.NET_RATE_COLUMN &&
      lineItems.some(
        (lineItem) =>
          !isNil(isRebill ? lineItem.rebillNetTotalAmount : lineItem.netTotalAmount),
      );
    const commissionSummary = presentation?.commissionSummary;
    const commissionDeduction = isRebill
      ? commissionSummary?.rebillAmount
      : commissionSummary?.amount;
    const netColumnHeaders = useMemo(
      () =>
        showNetColumns
          ? [
              t('pay_commission_statement.commission'),
              t('pay_commission_statement.net_rate'),
            ]
          : [],
      [showNetColumns],
    );
    const settlementAddOnCharges = useMemo(() => {
      return settlementsStore.addOnChargesBySettlementId[settlementId] ?? [];
    }, [settlementId, settlementsStore.addOnChargesBySettlementId]);

    // When adding columns here, update the SettlementLineItems component to add the new column values
    const headerColumns = useMemo(() => {
      return [
        t('approvals.driver_pay.settlements.pdf.date'),
        t('approvals.driver_pay.settlements.pdf.truck_num'),
        t('approvals.driver_pay.settlements.pdf.equipment_type'),
        t('approvals.driver_pay.settlements.pdf.equipment_id'),
        t('approvals.driver_pay.settlements.pdf.material'),
        ...(settlement.settlementLineItemType === SettlementLineItemType.LOAD
          ? [t('approvals.driver_pay.settlements.pdf.ticket_numbers')]
          : []),
        t('approvals.driver_pay.settlements.pdf.qty'),
        t('approvals.driver_pay.settlements.pdf.type'),
        t('approvals.driver_pay.settlements.pdf.rate_type'),
        t('approvals.driver_pay.settlements.pdf.billing_qty'),
        t('approvals.driver_pay.tabs.driver_pay.rate'),
        ...netColumnHeaders,
        t('approvals.driver_pay.settlements.pdf.total'),
      ];
    }, [settlement.settlementLineItemType, netColumnHeaders]);

    const isLoadType = settlement.settlementLineItemType === SettlementLineItemType.LOAD;

    const isSettlementExportLocked =
      Boolean(settlement && isSettlementAlreadyExported(settlement)) ||
      settlement?.rutterSettlementExport?.state === RutterExportState.PENDING ||
      settlement?.rutterSettlementExport?.state === RutterExportState.EXPORTED;
    const quickBooksItems = useSettlementQuickBooksItems(
      settlementId,
      isSettlementExportLocked,
    );
    const showQuickBooksItemColumn = quickBooksItems.isColumnVisible;
    const quickBooksItemColumn: QuickBooksItemColumn | undefined = useMemo(
      () =>
        showQuickBooksItemColumn
          ? {
              areMatchesLoaded: quickBooksItems.areMatchesLoaded,
              isMatching: quickBooksItems.isMatching,
              isReadOnly: isSettlementExportLocked,
              itemOptions: quickBooksItems.itemOptions,
              matchesByAddOnChargeId: quickBooksItems.matchesByAddOnChargeId,
              matchesBySettlementLineItemId:
                quickBooksItems.matchesBySettlementLineItemId,
              onRunMatching: quickBooksItems.runMatching,
              onSelect: quickBooksItems.saveItem,
            }
          : undefined,
      [
        isSettlementExportLocked,
        quickBooksItems.areMatchesLoaded,
        quickBooksItems.isMatching,
        quickBooksItems.itemOptions,
        quickBooksItems.matchesByAddOnChargeId,
        quickBooksItems.matchesBySettlementLineItemId,
        quickBooksItems.runMatching,
        quickBooksItems.saveItem,
        showQuickBooksItemColumn,
      ],
    );

    const rerunMatchingLabel = t(
      'approvals.driver_pay.settlements.quick_books_item_rerun_matching',
    );

    const quickBooksSelectWidthPx = useMemo(
      () => getMappingSelectWidthPx(quickBooksItems.itemOptions),
      [quickBooksItems.itemOptions],
    );

    const gridConfig = useMemo(() => {
      // +1 for Total's span 2; +1 more for LOAD Ticket Numbers span 2
      const columns = headerColumns.length + 1 + (isLoadType ? 1 : 0);
      const addonOffset = columns - 4 - netColumnHeaders.length;
      const netColumnAreas = ' .'.repeat(netColumnHeaders.length);
      const tracks = Array<string>(columns).fill('minmax(0, 1fr)');
      // Ticket Numbers' second track is spare room; the action track holds the remove icon
      const ticketNumbersSecondTrackIndex = 6;
      if (isLoadType) {
        tracks[ticketNumbersSecondTrackIndex] = 'minmax(0, 0.25fr)';
      }
      tracks[columns - 1] = `${ACTION_TRACK_WIDTH_PX}px`;
      if (showQuickBooksItemColumn) {
        tracks.push(`${quickBooksSelectWidthPx + QUICK_BOOKS_ICON_SLOT_PX}px`);
      }
      const quickBooksArea = showQuickBooksItemColumn ? ' qb' : '';

      return {
        ...COMMON_GRID_CONFIG,
        gridTemplateColumns: tracks.join(' '),
        gridTemplateAreas: `"${Array(addonOffset).fill('.').join(' ')} qty rate${netColumnAreas} total action${quickBooksArea}"`,
      };
    }, [
      headerColumns.length,
      isLoadType,
      netColumnHeaders,
      quickBooksSelectWidthPx,
      showQuickBooksItemColumn,
    ]);

    const formMethods = useForm<AddOnChargesDTO>({
      defaultValues: {
        addOnCharges: settlementAddOnCharges.map((addOnCharge) => {
          return schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge);
        }),
      },
      mode: 'all',
      delayError: 500,
    });
    const {
      control,
      handleSubmit,
      formState: { isValid, isDirty },
      reset,
    } = formMethods;

    useEffect(() => {
      reset({
        addOnCharges: settlementAddOnCharges.map((addOnCharge) => {
          return schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge);
        }),
      });
    }, [settlementAddOnCharges]);
    const watchedAddOnCharges = useWatch({ control, name: 'addOnCharges' });

    const {
      getAllAddOns,
      isLoading: isLoadingAllAdOns,
      isCreating: isCreatingSettlementAddOnCharges,
      batchUpdateAddOnChargesOfSettlements,
      isUpdating: isUpdatingSettlementAddOnCharges,
    } = useAddOns();
    const { getAllSettlementAddOnCharges } = useSettlements();
    const [isLoadingAllSettlementAddOnCharges, setIsLoadingAllSettlementAddOnCharges] =
      useState(false);
    const { getAllSettlementLineItems, isLoading: isLoadingAllSettlementLineItems } =
      useSettlementLineItems();
    const { currencyFormatter } = useCompanyCurrency();

    const allowsEmails = useMemo(() => canSendEmail(settlement), [settlement]);

    // Compute grouped structure when line items or add-on charges change
    const groupedResult = useMemo(() => {
      return groupLineItemsByOrderJob(lineItems, settlementAddOnCharges);
    }, [lineItems, settlementAddOnCharges]);

    const [enrichedOrderGroups, setEnrichedOrderGroups] = useState<OrderGroup[]>([]);
    const { getOrderById } = useOrders();
    // Use ref to avoid infinite loop - getOrderById is not memoized in useOrders
    const getOrderByIdRef = useRef(getOrderById);
    getOrderByIdRef.current = getOrderById;

    useEffect(() => {
      const fetchOrderDetails = async () => {
        if (groupedResult.orderGroups.length === 0) {
          setEnrichedOrderGroups([]);
          return;
        }

        const orderIds = extractUniqueOrderIds(groupedResult.orderGroups);
        const displayDataMap = await fetchOrderDisplayData(
          orderIds,
          getOrderByIdRef.current,
        );
        const enriched = enrichOrderGroupsWithDisplayData(
          groupedResult.orderGroups,
          displayDataMap,
        );
        setEnrichedOrderGroups(enriched);
      };

      fetchOrderDetails();
    }, [groupedResult.orderGroups]);

    // Hydrate line items and add on charges on mount
    useEffect(() => {
      const getSettlementLineItems = async () => {
        try {
          const lineItemsRequest = await getAllSettlementLineItems(
            companyId,
            String(settlementId),
          );
          const lineItems = lineItemsRequest.map((group) => {
            return group.lineItems;
          });
          setLineItems(lineItems.flat());
        } catch {
          // Error already handled in the hook.
        }
      };

      const getSettlementAddOnCharges = async () => {
        setIsLoadingAllSettlementAddOnCharges(true);
        try {
          await getAllSettlementAddOnCharges(companyId, String(settlementId));
        } catch {
          // Error already handled in the hook.
        } finally {
          setIsLoadingAllSettlementAddOnCharges(false);
        }
      };

      void getSettlementLineItems();
      void getSettlementAddOnCharges();
    }, [
      companyId,
      getAllSettlementAddOnCharges,
      getAllSettlementLineItems,
      settlementId,
    ]);

    // Hydrate select options on mount
    useEffect(() => {
      const hydrateAddOnOptions = async () => {
        try {
          await getAllAddOns(companyId);
        } catch {
          // Error already handled in the hook.
        }
      };

      void hydrateAddOnOptions();
    }, [companyId]);

    const isLoadingAddOnArea = useMemo(
      () =>
        isLoadingAllAdOns ||
        isLoadingAllSettlementAddOnCharges ||
        isLoadingAllSettlementLineItems,
      [
        isLoadingAllAdOns,
        isLoadingAllSettlementAddOnCharges,
        isLoadingAllSettlementLineItems,
      ],
    );

    const addOnByAddOnId = useMemo(() => {
      const addOnByAddOnId = addOnsStore.addOns.reduce(
        (acc, addOn) => {
          acc[addOn.id ?? ''] = addOn;
          return acc;
        },
        {} as Record<string, AddOn>,
      );
      return addOnByAddOnId;
    }, [settlementId, addOnsStore.addOns]);

    const handleAddAddOnClick = useCallback(() => {
      addOnChargesRef.current.append?.();
      setIsEditing(true);
    }, [setIsEditing]);

    const isReadyForSubmission = useMemo(
      () => (watchedAddOnCharges?.length ?? 0) > 0 && isDirty && isValid,
      [isValid, isDirty, watchedAddOnCharges],
    );

    const canEditSettlement = useMemo(() => settlement.editable, [settlement.editable]);

    const onSubmit = useCallback(
      handleSubmit(async () => {
        const addOnCharges = (await addOnChargesRef.current.onSubmit?.()) ?? [];
        const previousAddOnChargeIds = new Set(
          settlementAddOnCharges.map((addOnCharge) => addOnCharge.id),
        );
        const { addOnCharges: savedAddOnCharges, settlement: refreshedSettlement } =
          await batchUpdateAddOnChargesOfSettlements(
            companyId,
            settlementId,
            addOnCharges,
          );
        if (refreshedSettlement) {
          onSettlementChange(refreshedSettlement);
        }
        const addedAddOnChargeIds = savedAddOnCharges.flatMap((addOnCharge) =>
          addOnCharge.id && !previousAddOnChargeIds.has(addOnCharge.id)
            ? [addOnCharge.id]
            : [],
        );
        quickBooksItems.awaitAddOnMatching(addedAddOnChargeIds);
        setIsEditing(false);
        toasterStore.push(
          alert(
            t('approvals.driver_pay.settlements.add_ons_updated_successfully'),
            AlertTypes.success,
          ),
        );
      }),
      [
        setIsEditing,
        settlementId,
        addOnByAddOnId,
        companyId,
        onSettlementChange,
        quickBooksItems.awaitAddOnMatching,
        settlementAddOnCharges,
      ],
    );

    return (
      <FormProvider {...formMethods}>
        <Box
          sx={{
            border: 'none',
            px: 1,
            pb: 1,
            backgroundColor: theme.brandV2.colors.treadGray8,
            display: 'grid',
          }}
        >
          <Box sx={{ gridColumn: '1 / -1' }}>
            <ExpandedBlockTabs value={selectedTab} onChange={handleSelectedTabChange}>
              {visibleTabs.map((tab) => (
                <Tab key={tab.key} value={tab.key} label={tab.label} />
              ))}
            </ExpandedBlockTabs>
          </Box>

          {selectedTab === SettlementDetailsTab.LINE_ITEMS && (
            <>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Box
                  sx={{
                    ...gridConfig,
                    gridTemplateRows: '36px',
                    bgcolor: theme.semantic.surfaceSunken,
                  }}
                >
                  {headerColumns.map((header, index, arr) => {
                    const ticketNumbersText = t(
                      'approvals.driver_pay.settlements.pdf.ticket_numbers',
                    );
                    const shouldSpan =
                      index === arr.length - 1 || header === ticketNumbersText;

                    return (
                      <Typography
                        key={header}
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          ...(shouldSpan ? { gridColumn: 'span 2' } : {}),
                        }}
                      >
                        {header}
                      </Typography>
                    );
                  })}
                  {showQuickBooksItemColumn && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        gridArea: 'qb',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t(
                          'administration.integration.rutter.quick_books_online.tread_first.columns.quick_books_item',
                        )}
                      </Typography>
                      {!isSettlementExportLocked && (
                        <Tooltip title={rerunMatchingLabel}>
                          <span>
                            <IconButton
                              aria-label={rerunMatchingLabel}
                              data-test-id="settlement-quick-books-item-rerun-matching"
                              disabled={
                                !quickBooksItems.areMatchesLoaded ||
                                quickBooksItems.isMatching
                              }
                              onClick={() => void quickBooksItems.runMatching()}
                              size="small"
                            >
                              {quickBooksItems.isMatching ? (
                                <CircularProgress size={18} />
                              ) : (
                                <RefreshIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>

                {isLoadingAddOnArea ? (
                  <LoadingPlaceholder />
                ) : (
                  <>
                    <GroupedSettlementLineItems
                      orderGroups={
                        enrichedOrderGroups.length > 0
                          ? enrichedOrderGroups
                          : groupedResult.orderGroups
                      }
                      ungroupedAddOnCharges={groupedResult.ungroupedAddOnCharges}
                      settlement={settlement}
                      currencyFormatter={currencyFormatter}
                      gridConfig={gridConfig}
                      isRebill={isRebill}
                      showNetColumns={showNetColumns}
                      isEditing={isEditing}
                      quickBooksItemColumn={quickBooksItemColumn}
                    />
                    {showNetColumns &&
                      settlementLineItemsStore.settlementLineItemGroupsBySettlementId[
                        settlementId
                      ]?.map((group, index) => {
                        const netSubtotal = isRebill
                          ? group.rebillNetSubtotal
                          : group.netSubtotal;
                        const label = t('pay_commission_statement.net_subtotal');

                        return (
                          !isNil(netSubtotal) && (
                            <Box
                              key={group.group ?? index}
                              sx={{
                                gridColumn: '1 / -1',
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography>
                                {group.group ? `${group.group}: ${label}` : label}
                              </Typography>
                              <Typography>{currencyFormatter(netSubtotal)}</Typography>
                            </Box>
                          )
                        );
                      })}
                    {commissionSummary && !isNil(commissionDeduction) && (
                      <Box
                        sx={{
                          ...gridConfig,
                          borderBottom: (theme) => `1px solid ${theme.semantic.border}`,
                        }}
                      >
                        <Typography variant="body2" sx={{ gridColumn: '1 / 4' }}>
                          {commissionSummary.label}
                        </Typography>
                        <Typography variant="body2" sx={{ gridArea: 'total' }}>
                          {currencyFormatter(commissionDeduction)}
                        </Typography>
                      </Box>
                    )}
                    {quickBooksItems.hasAiSuggestedMatch && showQuickBooksItemColumn && (
                      <Box
                        sx={{
                          gridColumn: '1 / -1',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          bgcolor: 'background.paper',
                          px: 1,
                          py: 1,
                        }}
                      >
                        <AutoAwesomeIcon
                          fontSize="small"
                          sx={{ color: 'primary.main' }}
                        />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t('approvals.driver_pay.settlements.quick_books_item_ai_note')}
                        </Typography>
                      </Box>
                    )}
                    {/* In edit mode, show editable settlement add-ons section at bottom */}
                    {isEditing && (
                      <>
                        <Box
                          sx={{
                            gridColumn: '1 / -1',
                            bgcolor: theme.semantic.surfaceSunken,
                            px: 1,
                            py: 1,
                            borderTop: `2px solid ${theme.brandV2.colors.treadGray5}`,
                            mt: 2,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {t('approvals.driver_pay.settlements.settlement_add_ons')}
                          </Typography>
                        </Box>
                        <SettlementAddOnCharges
                          settlementId={settlementId}
                          settlementAddOnCharges={groupedResult.ungroupedAddOnCharges}
                          currencyFormatter={currencyFormatter}
                          isEditing={isEditing}
                          ref={addOnChargesRef}
                          gridConfig={gridConfig}
                          isRebill={!!settlement.originalSettlement}
                          settlement={settlement}
                        />
                      </>
                    )}
                  </>
                )}

                {canEditSettlement && (
                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      bgcolor: 'background.paper',
                      py: 1.5,
                      px: 1,
                      mb: 1,
                    }}
                  >
                    {isEditing ? (
                      <>
                        <SmallButton
                          color="brandV2Yellow"
                          onClick={handleAddAddOnClick}
                          startIcon={<AddIcon />}
                          disabled={isUpdatingSettlementAddOnCharges}
                        >
                          {t('approvals.driver_pay.settlements.add_on')}
                        </SmallButton>
                        <Box
                          component="form"
                          onSubmit={onSubmit}
                          sx={{
                            display: 'flex',
                            gap: 1,
                          }}
                        >
                          <SmallButton
                            type="button"
                            color="secondary"
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={() => {
                              setIsEditing(false);
                              reset();
                            }}
                            sx={{
                              fontWeight: 500,
                              '&.MuiButton-root': {
                                borderColor: theme.brandV2.colors.treadGray7,
                              },
                            }}
                          >
                            {t('approvals.driver_pay.settlements.cancel')}
                          </SmallButton>
                          <SmallLoadingButton
                            type="submit"
                            color="brandV2Green"
                            loading={isCreatingSettlementAddOnCharges}
                            disabled={!isReadyForSubmission}
                            startIcon={<CheckIcon />}
                          >
                            {t('actions.save_changes')}
                          </SmallLoadingButton>
                        </Box>
                      </>
                    ) : (
                      <SmallButton
                        color="brandV2Yellow"
                        onClick={() => setIsEditing(true)}
                        startIcon={<EditOutlinedIcon />}
                      >
                        {t('approvals.driver_pay.settlements.edit_add_ons')}
                      </SmallButton>
                    )}
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                  }}
                >
                  {allowsEmails && (
                    <SettlementOutboundEmails
                      lastOutboundEmail={settlement.lastOutboundEmail ?? null}
                      settlementId={settlementId}
                    />
                  )}
                  <SettlementVoidReason voidReason={settlement.voidReason ?? null} />
                  <SettlementNotes
                    onSettlementChange={onSettlementChange}
                    settlement={settlement}
                  />
                  <SettlementDueDate
                    onSettlementChange={onSettlementChange}
                    settlement={settlement}
                  />
                </Box>
                <SettlementPricing settlement={settlement} />
              </Box>
            </>
          )}

          {selectedTab === SettlementDetailsTab.LOADS && (
            <SettlementLoadsTab settlement={settlement} />
          )}

          {selectedTab === SettlementDetailsTab.LINES && settlement && (
            <SettlementLinesTab
              onSettlementChange={onSettlementChange}
              settlement={settlement}
            />
          )}
        </Box>
      </FormProvider>
    );
  },
);
